import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import BrandLogo from './BrandLogo';

/**
 * Modern Animated Hamburger Icon
 * Blocksy-inspired with smooth morphing animation
 */
export const HamburgerIcon = ({ 
  isOpen, 
  onClick, 
  color = 'currentColor',
  size = 24,
  lineWidth = 2,
  style = 'default' // 'default', 'spin', 'squeeze', 'elastic'
}) => {
  const getAnimationClass = () => {
    switch (style) {
      case 'spin':
        return isOpen ? 'rotate-180' : 'rotate-0';
      case 'squeeze':
        return isOpen ? 'scale-90' : 'scale-100';
      case 'elastic':
        return isOpen ? 'scale-110' : 'scale-100';
      default:
        return '';
    }
  };

  return (
    <button
      onClick={onClick}
      className={`relative flex items-center justify-center p-2 rounded-lg transition-all duration-300 hover:bg-black/5 dark:hover:bg-white/10 ${getAnimationClass()}`}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <div className="relative" style={{ width: size, height: size }}>
        {/* Top line */}
        <span
          className={`absolute left-0 block rounded-full transition-all duration-300 ease-in-out`}
          style={{
            backgroundColor: color,
            height: lineWidth,
            width: size,
            top: isOpen ? '50%' : '25%',
            transform: isOpen 
              ? 'translateY(-50%) rotate(45deg)' 
              : 'translateY(-50%) rotate(0deg)',
          }}
        />
        {/* Middle line */}
        <span
          className={`absolute left-0 block rounded-full transition-all duration-200 ease-in-out`}
          style={{
            backgroundColor: color,
            height: lineWidth,
            width: isOpen ? 0 : size * 0.7,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: isOpen ? 0 : 1,
          }}
        />
        {/* Bottom line */}
        <span
          className={`absolute left-0 block rounded-full transition-all duration-300 ease-in-out`}
          style={{
            backgroundColor: color,
            height: lineWidth,
            width: size,
            top: isOpen ? '50%' : '75%',
            transform: isOpen 
              ? 'translateY(-50%) rotate(-45deg)' 
              : 'translateY(-50%) rotate(0deg)',
          }}
        />
      </div>
    </button>
  );
};

/**
 * Modern Full-Screen Mobile Menu
 * Blocksy-inspired with smooth animations and customizable layout
 */
const MobileMenu = ({
  isOpen,
  onClose,
  navLinks = [],
  actions = [],
  settings = {},
  brandName = 'ISOcore',
}) => {
  const location = useLocation();
  const menuRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Default mobile menu settings
  const {
    position = 'right', // 'left', 'right', 'top', 'fullscreen'
    width = '320', // px for left/right
    bgColor = '#ffffff',
    textColor = '#0f172a',
    overlayColor = 'rgba(0, 0, 0, 0.5)',
    overlayBlur = true,
    showLogo = true,
    showCloseButton = true,
    closeButtonPosition = 'right', // 'left', 'right'
    animation = 'slide', // 'slide', 'fade', 'push'
    animationDuration = '300',
    linkStyle = 'default', // 'default', 'bordered', 'filled'
    linkSpacing = '16', // px
    linkFontSize = '16',
    linkFontWeight = '500',
    showDividers = true,
    dividerColor = '#e2e8f0',
    buttonLayout = 'stacked', // 'stacked', 'inline'
    buttonSpacing = '12',
    headerHeight = '64',
    footerText = '',
    socialLinks = [],
  } = settings;

  // Close on route change
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Get position styles
  const getPositionStyles = () => {
    const base = {
      position: 'fixed',
      zIndex: 50,
      backgroundColor: bgColor,
      color: textColor,
      transition: `transform ${animationDuration}ms ease-in-out, opacity ${animationDuration}ms ease-in-out`,
    };

    switch (position) {
      case 'left':
        return {
          ...base,
          top: 0,
          left: 0,
          bottom: 0,
          width: `${width}px`,
          maxWidth: '100vw',
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        };
      case 'top':
        return {
          ...base,
          top: 0,
          left: 0,
          right: 0,
          maxHeight: '80vh',
          transform: isOpen ? 'translateY(0)' : 'translateY(-100%)',
        };
      case 'fullscreen':
        return {
          ...base,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transform: isOpen ? 'scale(1)' : 'scale(0.95)',
          opacity: isOpen ? 1 : 0,
        };
      case 'right':
      default:
        return {
          ...base,
          top: 0,
          right: 0,
          bottom: 0,
          width: `${width}px`,
          maxWidth: '100vw',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        };
    }
  };

  const getLinkStyles = () => {
    const base = {
      display: 'block',
      padding: `${linkSpacing}px 0`,
      fontSize: `${linkFontSize}px`,
      fontWeight: linkFontWeight,
      color: textColor,
      transition: 'all 0.2s ease',
    };

    switch (linkStyle) {
      case 'bordered':
        return {
          ...base,
          padding: `${linkSpacing}px 16px`,
          border: `1px solid ${dividerColor}`,
          borderRadius: '8px',
          marginBottom: '8px',
        };
      case 'filled':
        return {
          ...base,
          padding: `${linkSpacing}px 16px`,
          backgroundColor: 'rgba(0,0,0,0.03)',
          borderRadius: '8px',
          marginBottom: '8px',
        };
      default:
        return base;
    }
  };

  // Actions come from CMS via BrandSettingsContext - no fallback here
  const displayActions = actions || [];

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: overlayColor,
          backdropFilter: overlayBlur ? 'blur(4px)' : 'none',
          WebkitBackdropFilter: overlayBlur ? 'blur(4px)' : 'none',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div
        ref={menuRef}
        style={getPositionStyles()}
        className="flex flex-col shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 border-b"
          style={{ 
            height: `${headerHeight}px`,
            borderColor: dividerColor,
          }}
        >
          {closeButtonPosition === 'left' && showCloseButton && (
            <button
              onClick={onClose}
              className="p-2 -ml-2 rounded-lg hover:bg-black/5 transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          )}
          
          {showLogo && (
            <div className={closeButtonPosition === 'left' ? 'flex-1 flex justify-center' : ''}>
              <Link to="/" onClick={onClose}>
                <BrandLogo variant="default" />
              </Link>
            </div>
          )}
          
          {closeButtonPosition === 'right' && showCloseButton && (
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-lg hover:bg-black/5 transition-colors ml-auto"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto px-6 py-4">
          <ul className="space-y-1">
            {navLinks.map((link, index) => {
              const hasChildren = link.children && link.children.length > 0;
              const isExpanded = expandedItems[link.id];

              return (
                <li key={link.id || index}>
                  {hasChildren ? (
                    <div>
                      <button
                        onClick={() => toggleExpanded(link.id)}
                        className="w-full flex items-center justify-between hover:opacity-70"
                        style={getLinkStyles()}
                      >
                        <span>{link.label}</span>
                        <ChevronDown 
                          size={20} 
                          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${
                          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <ul className="pl-4 py-2 space-y-1">
                          {link.children.map((child, childIndex) => (
                            <li key={child.id || childIndex}>
                              <Link
                                to={child.href}
                                onClick={onClose}
                                className="flex items-center py-3 text-sm opacity-80 hover:opacity-100"
                              >
                                <ChevronRight size={16} className="mr-2 opacity-50" />
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Link
                        to={link.href}
                        onClick={onClose}
                        style={getLinkStyles()}
                        className="hover:opacity-70"
                      >
                        {link.label}
                      </Link>
                      {showDividers && linkStyle === 'default' && (
                        <div 
                          className="h-px"
                          style={{ backgroundColor: dividerColor }}
                        />
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Action Buttons */}
        <div 
          className={`px-6 py-4 border-t ${
            buttonLayout === 'inline' ? 'flex gap-3' : 'space-y-3'
          }`}
          style={{ 
            borderColor: dividerColor,
            gap: `${buttonSpacing}px`,
          }}
        >
          {displayActions.map((action) => {
            const isExternal = action.href?.startsWith('http');
            
            if (isExternal) {
              return (
                <a 
                  key={action.id} 
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className={buttonLayout === 'inline' ? 'flex-1' : 'block'}
                >
                  <Button 
                    variant={action.variant === 'primary' ? 'default' : 'outline'}
                    className="w-full justify-center"
                    size="lg"
                  >
                    {action.label}
                  </Button>
                </a>
              );
            }
            
            return (
              <Link 
                key={action.id} 
                to={action.href}
                onClick={onClose}
                className={buttonLayout === 'inline' ? 'flex-1' : 'block'}
              >
                <Button 
                  variant={action.variant === 'primary' ? 'default' : 'outline'}
                  className="w-full justify-center"
                  size="lg"
                >
                  {action.label}
                </Button>
              </Link>
            );
          })}
        </div>

        {/* Footer */}
        {footerText && (
          <div 
            className="px-6 py-4 text-center text-sm opacity-60 border-t"
            style={{ borderColor: dividerColor }}
          >
            {footerText}
          </div>
        )}
      </div>
    </>
  );
};

export default MobileMenu;
